// Citas de la Agenda (objeto cita de la app): reuniones con clientes, con su responsable, contacto y estado.
import { request } from './http';
import { create, f, listAll, remove, update } from './rest';

export type EstadoCita = 'PROGRAMADA' | 'CONFIRMADA' | 'REPROGRAMAR' | 'CANCELADA' | 'REALIZADA' | 'NO_ASISTIO';
// De donde vino (D49): a mano, de una invitacion de calendario que llego por correo, o de una reunion de 3CX.
export type OrigenCita = 'MANUAL' | 'INVITACION' | 'TRESCX';
export type Cita = {
  id: string;
  titulo: string;
  inicio: string;
  fin: string | null;
  lugar: string;
  estado: EstadoCita;
  responsableId: string | null;
  notas: string;
  personaId: string | null;
  empresaId: string | null;
  origen: OrigenCita;
  enlace: string;
  organizador: string;
  icsUid: string;
  // De donde salio una invitacion (D49): buzon donde llego, su correo en correo-api y el caso que abrio.
  buzon: string;
  invitacionId: number | null;
  casoId: string | null;
  persona: { id: string; nombre: string } | null;
  empresa: { id: string; nombre: string } | null;
  // Cuando se creo en el CRM (para avisar de invitaciones nuevas, D52).
  creado: string | null;
};

type Raw = {
  id: string;
  name: string;
  inicio: string | null;
  fin: string | null;
  lugar: string | null;
  estado: EstadoCita | null;
  responsableId: string | null;
  notas: string | null;
  personaId: string | null;
  empresaId: string | null;
  origen?: OrigenCita | null;
  enlace?: string | null;
  organizador?: string | null;
  icsUid?: string | null;
  buzon?: string | null;
  invitacionId?: number | null;
  casoId?: string | null;
  persona?: { id: string; name?: { firstName?: string; lastName?: string } } | null;
  empresa?: { id: string; name?: string } | null;
  createdAt?: string | null;
};

const aCita = (r: Raw): Cita => ({
  id: r.id,
  titulo: r.name || 'Cita',
  inicio: r.inicio ?? new Date().toISOString(),
  fin: r.fin,
  lugar: r.lugar ?? '',
  estado: r.estado ?? 'PROGRAMADA',
  responsableId: r.responsableId,
  notas: r.notas ?? '',
  personaId: r.personaId,
  empresaId: r.empresaId,
  origen: r.origen ?? 'MANUAL',
  enlace: r.enlace ?? '',
  organizador: r.organizador ?? '',
  icsUid: r.icsUid ?? '',
  buzon: r.buzon ?? '',
  invitacionId: r.invitacionId ?? null,
  casoId: r.casoId ?? null,
  persona: r.persona ? { id: r.persona.id, nombre: `${r.persona.name?.firstName ?? ''} ${r.persona.name?.lastName ?? ''}`.trim() || 'Contacto' } : null,
  empresa: r.empresa ? { id: r.empresa.id, nombre: r.empresa.name ?? 'Empresa' } : null,
  creado: r.createdAt ?? null,
});

export const citasEntre = async (desde: Date, hasta: Date, max = 1000) =>
  (await listAll<Raw>('citas', { filter: f.and(f.gte('inicio', desde.toISOString()), f.lt('inicio', hasta.toISOString())), orderBy: 'inicio[AscNullsLast]', depth: 1 }, max)).map(aCita);

// Citas creadas desde una fecha (invitaciones y reuniones de 3CX nuevas para la campana, D52).
export const citasCreadasDesde = async (desde: Date, max = 200) =>
  (await listAll<Raw>('citas', { filter: f.gte('createdAt', desde.toISOString()), orderBy: 'createdAt[DescNullsLast]', depth: 1 }, max)).map(aCita);

export const citasDe = async (campo: 'personaId' | 'empresaId', id: string) =>
  (await listAll<Raw>('citas', { filter: f.eq(campo, id), orderBy: 'inicio[DescNullsLast]', depth: 1 }, 200)).map(aCita);

export type CitaInput = {
  titulo: string;
  inicio: string;
  fin: string | null;
  lugar: string;
  estado: EstadoCita;
  responsableId: string | null;
  notas: string;
  personaId: string | null;
  empresaId: string | null;
  enlace?: string;
  // Solo al crear una reunion de 3CX desde la Agenda.
  origen?: OrigenCita;
  icsUid?: string;
};
const cuerpo = (c: CitaInput) => ({
  name: c.titulo.trim(),
  inicio: c.inicio,
  fin: c.fin,
  lugar: c.lugar,
  estado: c.estado,
  responsableId: c.responsableId,
  notas: c.notas,
  personaId: c.personaId,
  empresaId: c.empresaId,
  ...(c.enlace !== undefined ? { enlace: c.enlace } : {}),
  ...(c.origen ? { origen: c.origen } : {}),
  ...(c.icsUid ? { icsUid: c.icsUid } : {}),
});
export const crearCita = (c: CitaInput) => create('citas', cuerpo(c));
export const editarCita = (id: string, c: CitaInput) => update('citas', id, cuerpo(c));
export const borrarCita = (id: string) => remove('citas', id);

export const ESTADOS_CITA: [EstadoCita, string, 'acc' | 'ok' | 'warn' | 'bad' | undefined][] = [
  ['PROGRAMADA', 'Programada', 'acc'],
  ['CONFIRMADA', 'Confirmada', 'ok'],
  ['REPROGRAMAR', 'Reprogramar', 'warn'],
  ['CANCELADA', 'Cancelada', undefined],
  ['REALIZADA', 'Realizada', 'ok'],
  ['NO_ASISTIO', 'No asistió', 'bad'],
];

// El correo original de una invitacion (lo trae correo-api del buzon; el del buzon personal de otro no se puede ver).
export type CorreoInvitacion = { buzon: string; asunto: string; de: string; para: string; cc: string; fecha: string; texto: string };
export const correoDeInvitacion = (id: number) => request<CorreoInvitacion>(`/correo/api/invitacion/${id}`);

// "Lo tomo": la cita sin responsable pasa a quien la toma.
export const tomarCita = (id: string, miembroId: string) => update('citas', id, { responsableId: miembroId });

// Crea una reunion de video en la central 3CX (D49); 3CX invita a los participantes. La cita se guarda aparte.
export const crearReunion3cx = (r: { titulo: string; inicio: string; duracion: number; descripcion?: string; extension?: string; tipo?: 'reunion' | 'webinar'; invitados: { nombre?: string; correo: string }[] }) =>
  request<{ icsUid: string; enlace: string; meetingid: string }>('/s/3cx/reunion', { method: 'POST', body: r });

// Lo que se cambia o cancela en la Agenda de una reunion de la central 3CX tambien se hace alla (D97); 3CX avisa a los
// participantes. Se ubica por el icsUid (3cx:<id>) o por el enlace de la invitacion. enCentral false: no es de nuestra
// central (o ya no esta), y solo se cambia la cita.
export const cambiarReunion3cx = (r: { accion: 'cambiar' | 'borrar'; icsUid?: string; enlace?: string; titulo?: string; inicio?: string; duracion?: number }) =>
  request<{ enCentral: boolean; mensaje?: string }>('/s/3cx/reunion/cambiar', { method: 'POST', body: r });
/** Si la cita puede ser una reunion de la central 3CX: creada en 3CX o una invitacion con enlace /meet/. */
export const esReunion3cx = (c: Pick<Cita, 'origen' | 'icsUid' | 'enlace'>) => c.icsUid.startsWith('3cx:') || /\/meet\//.test(c.enlace);

export { inicioSemana } from '../lib/agenda';
